import React, { useEffect, useRef, useState } from 'react';
import { X, Save, Loader2, Camera, Upload, AlertCircle, CheckCircle2, Lock, UserCheck, Users } from 'lucide-react';
import { atlasApi, AtlasUser, Colaborador } from '../shared/atlasApi';
import AvatarColaborador from '../shared/AvatarColaborador';
import AbaPessoais from './abas/AbaPessoais';
import AbaFamilia from './abas/AbaFamilia';
import AbaFormacao from './abas/AbaFormacao';
import AbaUniforme from './abas/AbaUniforme';
import AbaDocumentos from './abas/AbaDocumentos';

interface Props {
  user: AtlasUser;
  colaborador: Colaborador;
  podeVerSensiveis: boolean;
  onClose: () => void;
  onSalvo: () => void;
}

type Aba = 'foto' | 'contatos' | 'pessoais' | 'familia' | 'uniforme' | 'formacao' | 'organizacao' | 'documentos' | 'sensiveis';

const EMAIL_RE = /^[^@]+@[^@]+\.[^@]+$/;
const FOTO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

function mascararCPF(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

const inputCls =
  'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-400';
const labelCls = 'text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 block';

const ModalEditarFicha: React.FC<Props> = ({ user, colaborador, podeVerSensiveis, onClose, onSalvo }) => {
  const d = colaborador.dados_publicos;
  const e = colaborador.enriquecimento || {};

  const [aba, setAba] = useState<Aba>('contatos');
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'ok' | 'erro'; msg: string } | null>(null);

  // --- Foto ---
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);

  // --- Contatos / enriquecimento ---
  const [contatos, setContatos] = useState({
    celular: e.celular || '',
    telefone_pessoal: e.telefone_pessoal || '',
    email_pessoal: e.email_pessoal || '',
    endereco_complemento: e.endereco_complemento || '',
    contato_emergencia: e.contato_emergencia || '',
    idiomas: e.idiomas || '',
    competencias: e.competencias || '',
    hobbies: e.hobbies || '',
    tamanho_camisa: e.tamanho_camisa || '',
    observacoes: e.observacoes || '',
  });

  // --- Sensíveis (LGPD) ---
  const [sensiveis, setSensiveis] = useState({
    cpf: mascararCPF(colaborador.cpf || ''),
    rg: colaborador.rg || '',
    salario: colaborador.salario || '',
  });

  // --- Organização (gestor + campos manuais) ---
  const ehManual = colaborador.origem === 'mongo';
  const [org, setOrg] = useState({
    nome: d.nome || '',
    matricula: d.matricula != null ? String(d.matricula) : '',
    cargo_nome: d.cargo_nome || '',
    centro_custo_nome: d.centro_custo_nome || '',
    departamento_codigo: d.departamento_codigo || '',
    email_corporativo: d.email_corporativo || '',
    data_admissao: (d.data_admissao || '').slice(0, 10),
    data_nascimento: (d.data_nascimento || '').slice(0, 10),
    status: d.status || 'ativo',
  });
  const [gestorFunccodi, setGestorFunccodi] = useState<number | null>(
    typeof e.gestor_funccodi === 'number' ? e.gestor_funccodi : null,
  );
  const [gestorNome, setGestorNome] = useState<string>('');
  const [gestorBusca, setGestorBusca] = useState('');
  const [gestorOpcoes, setGestorOpcoes] = useState<Colaborador[]>([]);
  const [gestorAberto, setGestorAberto] = useState(false);
  const buscaDebRef = useRef<any>(null);

  // Carrega nome do gestor atual
  useEffect(() => {
    if (gestorFunccodi == null) { setGestorNome(''); return; }
    let cancel = false;
    atlasApi.getColaborador(user, gestorFunccodi)
      .then(c => { if (!cancel) setGestorNome(c?.dados_publicos?.nome || `Mat. ${gestorFunccodi}`); })
      .catch(() => { if (!cancel) setGestorNome(`Mat. ${gestorFunccodi}`); });
    return () => { cancel = true; };
  }, [user, gestorFunccodi]);

  // Debounce busca de gestor
  useEffect(() => {
    if (!gestorAberto) return;
    if (buscaDebRef.current) clearTimeout(buscaDebRef.current);
    buscaDebRef.current = setTimeout(() => {
      atlasApi.listarColaboradores(user, { busca: gestorBusca || undefined, status: 'ativo', per_page: 8, page: 1 })
        .then(r => setGestorOpcoes(r.itens || []))
        .catch(() => setGestorOpcoes([]));
    }, 250);
    return () => { if (buscaDebRef.current) clearTimeout(buscaDebRef.current); };
  }, [user, gestorBusca, gestorAberto]);

  const setContato = (k: keyof typeof contatos, v: string) =>
    setContatos(prev => ({ ...prev, [k]: v }));

  const onSelecionarFoto = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(null);
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > FOTO_MAX_BYTES) {
      setFeedback({ tipo: 'erro', msg: 'Imagem excede 2 MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setFotoPreview(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => setFeedback({ tipo: 'erro', msg: 'Falha ao ler a imagem.' });
    reader.readAsDataURL(file);
  };

  const salvarFoto = async () => {
    if (!fotoPreview) {
      setFeedback({ tipo: 'erro', msg: 'Selecione uma imagem.' });
      return;
    }
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.uploadFoto(user, colaborador.funccodi, fotoPreview);
      setFeedback({ tipo: 'ok', msg: 'Foto atualizada com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao enviar foto.' });
    } finally {
      setSalvando(false);
    }
  };

  const salvarContatos = async () => {
    if (contatos.email_pessoal && !EMAIL_RE.test(contatos.email_pessoal)) {
      setFeedback({ tipo: 'erro', msg: 'E-mail pessoal inválido.' });
      return;
    }
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.editarColaborador(user, colaborador.funccodi, { ...contatos });
      setFeedback({ tipo: 'ok', msg: 'Contatos salvos com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const salvarOrganizacao = async () => {
    const body: Record<string, any> = {
      gestor_funccodi: gestorFunccodi,
    };
    if (ehManual) {
      if (!org.nome.trim()) {
        setFeedback({ tipo: 'erro', msg: 'Nome é obrigatório.' });
        return;
      }
      Object.assign(body, {
        nome: org.nome.trim(),
        matricula: org.matricula,
        cargo_nome: org.cargo_nome,
        centro_custo_nome: org.centro_custo_nome,
        departamento_codigo: org.departamento_codigo,
        email_corporativo: org.email_corporativo,
        data_admissao: org.data_admissao,
        data_nascimento: org.data_nascimento,
        status: org.status,
      });
    }
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.editarColaborador(user, colaborador.funccodi, body);
      setFeedback({ tipo: 'ok', msg: 'Organização salva com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const salvarSensiveis = async () => {
    const body: Record<string, any> = {};
    const cpfDigitos = sensiveis.cpf.replace(/\D/g, '');
    if (cpfDigitos) {
      if (cpfDigitos.length !== 11) {
        setFeedback({ tipo: 'erro', msg: 'CPF deve ter 11 dígitos.' });
        return;
      }
      body.cpf = cpfDigitos;
    }
    if (sensiveis.rg) body.rg = sensiveis.rg;
    if (sensiveis.salario) {
      const sal = parseFloat(String(sensiveis.salario).replace(',', '.'));
      if (!(sal > 0)) {
        setFeedback({ tipo: 'erro', msg: 'Salário deve ser um número positivo.' });
        return;
      }
      body.salario = sal;
    }
    if (Object.keys(body).length === 0) {
      setFeedback({ tipo: 'erro', msg: 'Preencha ao menos um campo.' });
      return;
    }
    setSalvando(true);
    setFeedback(null);
    try {
      await atlasApi.editarColaborador(user, colaborador.funccodi, body);
      setFeedback({ tipo: 'ok', msg: 'Dados sensíveis salvos com sucesso.' });
      onSalvo();
    } catch (err: any) {
      setFeedback({ tipo: 'erro', msg: err?.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const abas: { id: Aba; label: string }[] = [
    { id: 'foto', label: 'Foto' },
    { id: 'contatos', label: 'Contatos' },
    { id: 'pessoais', label: 'Pessoais' },
    { id: 'familia', label: 'Família' },
    { id: 'uniforme', label: 'Uniforme' },
    { id: 'formacao', label: 'Formação' },
    { id: 'organizacao', label: 'Organização' },
    ...(podeVerSensiveis ? [{ id: 'documentos' as Aba, label: 'Documentos' }] : []),
    ...(podeVerSensiveis ? [{ id: 'sensiveis' as Aba, label: 'Sensíveis (LGPD)' }] : []),
  ];

  return (
    <div
      className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        onClick={ev => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <AvatarColaborador
            nome={d.nome}
            fotoUrl={fotoPreview || ((d.tem_foto_gix || e.foto_url) ? atlasApi.fotoUrl(user, colaborador.funccodi, colaborador.data_sincronizacao) : undefined)}
            size={48}
          />
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900 truncate">{d.nome || 'Colaborador'}</h3>
            <p className="text-xs text-gray-400 truncate">Editar ficha · Mat. {d.matricula || colaborador.funccodi}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 px-5 pt-4">
          {abas.map(a => (
            <button
              key={a.id}
              onClick={() => { setAba(a.id); setFeedback(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                aba === a.id ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* Feedback */}
        {feedback && (
          <div className="px-5 pt-4">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                feedback.tipo === 'ok'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-rose-50 text-rose-700 border border-rose-200'
              }`}
            >
              {feedback.tipo === 'ok' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {feedback.msg}
            </div>
          </div>
        )}

        {/* Conteúdo */}
        <div className="p-5">
          {aba === 'foto' && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <AvatarColaborador
                  nome={d.nome}
                  fotoUrl={fotoPreview || ((d.tem_foto_gix || e.foto_url) ? atlasApi.fotoUrl(user, colaborador.funccodi, colaborador.data_sincronizacao) : undefined)}
                  size={88}
                />
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg cursor-pointer transition">
                    <Camera className="w-4 h-4" />
                    Escolher imagem
                    <input type="file" accept="image/*" className="hidden" onChange={onSelecionarFoto} />
                  </label>
                  <p className="text-xs text-gray-400 mt-2">JPEG, PNG ou WebP. Máximo 2 MB.</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={salvarFoto}
                  disabled={salvando || !fotoPreview}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition"
                >
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar foto
                </button>
              </div>
            </div>
          )}

          {aba === 'contatos' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Celular</label>
                  <input className={inputCls} value={contatos.celular} onChange={ev => setContato('celular', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Telefone pessoal</label>
                  <input className={inputCls} value={contatos.telefone_pessoal} onChange={ev => setContato('telefone_pessoal', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>E-mail pessoal</label>
                  <input className={inputCls} value={contatos.email_pessoal} onChange={ev => setContato('email_pessoal', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Contato de emergência</label>
                  <input className={inputCls} value={contatos.contato_emergencia} onChange={ev => setContato('contato_emergencia', ev.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Endereço (complemento)</label>
                  <input className={inputCls} value={contatos.endereco_complemento} onChange={ev => setContato('endereco_complemento', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Idiomas</label>
                  <input className={inputCls} value={contatos.idiomas} onChange={ev => setContato('idiomas', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Competências</label>
                  <input className={inputCls} value={contatos.competencias} onChange={ev => setContato('competencias', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Hobbies</label>
                  <input className={inputCls} value={contatos.hobbies} onChange={ev => setContato('hobbies', ev.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Tamanho de camisa</label>
                  <input className={inputCls} value={contatos.tamanho_camisa} onChange={ev => setContato('tamanho_camisa', ev.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Observações</label>
                <textarea
                  className={`${inputCls} min-h-[90px] resize-y`}
                  value={contatos.observacoes}
                  onChange={ev => setContato('observacoes', ev.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  Cancelar
                </button>
                <button
                  onClick={salvarContatos}
                  disabled={salvando}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
                >
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar
                </button>
              </div>
            </div>
          )}

          {aba === 'organizacao' && (
            <div className="space-y-4">
              {ehManual && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
                    <UserCheck className="w-4 h-4" /> Dados (colaborador manual)
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <label className={labelCls}>Nome completo *</label>
                      <input className={inputCls} value={org.nome} onChange={ev => setOrg(p => ({ ...p, nome: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Matrícula</label>
                      <input className={inputCls} value={org.matricula} onChange={ev => setOrg(p => ({ ...p, matricula: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>E-mail corporativo</label>
                      <input className={inputCls} value={org.email_corporativo} onChange={ev => setOrg(p => ({ ...p, email_corporativo: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Cargo</label>
                      <input className={inputCls} value={org.cargo_nome} onChange={ev => setOrg(p => ({ ...p, cargo_nome: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Centro de custo</label>
                      <input className={inputCls} value={org.centro_custo_nome} onChange={ev => setOrg(p => ({ ...p, centro_custo_nome: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Departamento (código)</label>
                      <input className={inputCls} value={org.departamento_codigo} onChange={ev => setOrg(p => ({ ...p, departamento_codigo: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Status</label>
                      <select className={inputCls} value={org.status} onChange={ev => setOrg(p => ({ ...p, status: ev.target.value as any }))}>
                        <option value="ativo">Ativo</option>
                        <option value="demitido">Demitido</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Data de admissão</label>
                      <input type="date" className={inputCls} value={org.data_admissao} onChange={ev => setOrg(p => ({ ...p, data_admissao: ev.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Data de nascimento</label>
                      <input type="date" className={inputCls} value={org.data_nascimento} onChange={ev => setOrg(p => ({ ...p, data_nascimento: ev.target.value }))} />
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-semibold text-purple-700 uppercase tracking-wider">
                  <Users className="w-4 h-4" /> Hierarquia
                </div>
                <label className={labelCls}>Gestor direto</label>
                {gestorFunccodi != null ? (
                  <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-purple-200 bg-purple-50">
                    <span className="text-sm text-gray-800 truncate">
                      {gestorNome || `Mat. ${gestorFunccodi}`}
                      <span className="text-gray-400 ml-2">#{gestorFunccodi}</span>
                    </span>
                    <button
                      onClick={() => { setGestorFunccodi(null); setGestorBusca(''); setGestorAberto(false); }}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      Remover
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      className={inputCls}
                      placeholder="Buscar gestor por nome ou matrícula..."
                      value={gestorBusca}
                      onFocus={() => setGestorAberto(true)}
                      onChange={ev => { setGestorBusca(ev.target.value); setGestorAberto(true); }}
                    />
                    {gestorAberto && gestorOpcoes.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                        {gestorOpcoes
                          .filter(c => c.funccodi !== colaborador.funccodi)
                          .map(c => (
                            <button
                              key={c._id}
                              onClick={() => {
                                setGestorFunccodi(c.funccodi);
                                setGestorNome(c.dados_publicos?.nome || '');
                                setGestorAberto(false);
                                setGestorBusca('');
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm"
                            >
                              <div className="font-medium text-gray-800 truncate">{c.dados_publicos?.nome}</div>
                              <div className="text-xs text-gray-400">
                                {c.dados_publicos?.cargo_nome || '—'} · Mat. {c.dados_publicos?.matricula || c.funccodi}
                              </div>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-400">Define a quem este colaborador se reporta no organograma.</p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  Cancelar
                </button>
                <button
                  onClick={salvarOrganizacao}
                  disabled={salvando}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
                >
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar organização
                </button>
              </div>
            </div>
          )}

          {aba === 'pessoais' && (
            <AbaPessoais user={user} colaborador={colaborador} podeEditar onSalvo={onSalvo} />
          )}

          {aba === 'familia' && (
            <AbaFamilia user={user} colaborador={colaborador} podeEditar onSalvo={onSalvo} />
          )}

          {aba === 'uniforme' && (
            <AbaUniforme user={user} colaborador={colaborador} podeEditar onSalvo={onSalvo} />
          )}

          {aba === 'formacao' && (
            <AbaFormacao user={user} colaborador={colaborador} podeEditar onSalvo={onSalvo} />
          )}

          {aba === 'documentos' && podeVerSensiveis && (
            <AbaDocumentos user={user} colaborador={colaborador} podeEditar onSalvo={onSalvo} />
          )}

          {aba === 'sensiveis' && podeVerSensiveis && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                Estes dados são criptografados antes de gravar.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>CPF</label>
                  <input
                    className={inputCls}
                    value={sensiveis.cpf}
                    placeholder="000.000.000-00"
                    onChange={ev => setSensiveis(p => ({ ...p, cpf: mascararCPF(ev.target.value) }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>RG</label>
                  <input className={inputCls} value={sensiveis.rg} onChange={ev => setSensiveis(p => ({ ...p, rg: ev.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Salário base (R$)</label>
                  <input
                    className={inputCls}
                    type="text"
                    inputMode="decimal"
                    value={sensiveis.salario}
                    placeholder="0.00"
                    onChange={ev => setSensiveis(p => ({ ...p, salario: ev.target.value }))}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  Cancelar
                </button>
                <button
                  onClick={salvarSensiveis}
                  disabled={salvando}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition"
                >
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Salvar dados sensíveis
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalEditarFicha;
